import type { MessagesShape } from '../types';
import type { Messages } from './ar';

export const fr: MessagesShape<Messages> = {
  app: {
    skipToContent: 'Aller au contenu',
    loading: 'Un instant…',
    back: 'Retour',
    close: 'Fermer',
    continue: 'Continuer',
    cancel: 'Annuler',
    confirm: 'Confirmer',
    save: 'Enregistrer',
    next: 'Suivant',
    start: 'Commencer',
    delete: 'Supprimer',
    yes: 'Oui',
    no: 'Non',
    sampleContent: 'Contenu d’exemple',
    sampleContentNotice:
      'Ces activités sont des exemples qui n’ont pas encore été validés par un enseignant et peuvent différer du programme de la classe.',
    offlineReady: '{app} fonctionne maintenant sans Internet.',
    updateAvailable: 'Une nouvelle version de {app} est disponible.',
    updateNow: 'Mettre à jour',
    later: 'Plus tard',
    mainNavigation: 'Navigation principale',
  },
  errors: {
    title: 'Un petit problème est survenu',
    body: 'Cette page n’a pas pu s’afficher. Ta progression est enregistrée.',
    reload: 'Recharger',
    goHome: 'Retour à l’accueil',
    notFound: 'Cette page est introuvable.',
    storageTitle: 'Impossible d’enregistrer la progression dans ce navigateur',
    storageUnavailable:
      'Le navigateur semble bloquer le stockage local (par exemple en navigation privée). Ouvre {app} dans une fenêtre normale.',
    storageQuota:
      'L’espace de stockage est plein. Libère de la place sur l’appareil puis réessaie.',
    storageBlocked:
      '{app} est ouvert dans un autre onglet avec une autre version. Ferme les autres onglets puis recharge.',
    storageUnknown: 'Le stockage local n’a pas pu être ouvert.',
    saveFailed:
      'La dernière réponse n’a pas pu être enregistrée. Tu peux continuer, mais cette séance risque de ne pas être sauvegardée.',
    contentMissing: 'Aucune activité n’est encore disponible ici.',
  },
  welcome: {
    title: 'Bienvenue sur {app}',
    subtitle:
      'De courts jeux éducatifs en arabe, mathématiques, langues et éveil scientifique, du préparatoire à la 6e année.',
    start: 'C’est parti !',
    privacyTitle: 'La vie privée de votre enfant d’abord',
    privacy1: 'Ni e-mail, ni numéro de téléphone, ni vrai nom.',
    privacy2: 'Toute la progression reste sur cet appareil.',
    privacy3: 'Aucune publicité, aucun traceur.',
    parentArea: 'Espace parent',
  },
  profiles: {
    title: 'Qui apprend aujourd’hui ?',
    add: 'Ajouter un enfant',
    play: 'Commencer avec {name}',
    anonymous: 'Élève',
    level: 'Classe : {level}',
    switch: 'Changer d’élève',
  },
  onboarding: {
    title: 'Nouveau profil',
    step: 'Étape {current} sur {total}',
    avatarTitle: 'Choisis ton personnage',
    nicknameLabel: 'Petit surnom (facultatif)',
    nicknameHint: 'Par exemple « Étoile » ou « Champion ». N’écris pas ton nom complet.',
    levelTitle: 'Dans quelle classe es-tu ?',
    levelHint: 'Le parent choisit la vraie classe de l’enfant, pas son âge.',
    schoolTypeTitle: 'Type d’école',
    schoolTypeHint:
      'Pour l’instant, nous utilisons le programme national pour les écoles publiques et privées.',
    variantTitle: 'Programme',
    languageTitle: 'Langue de l’application',
    preferencesTitle: 'Préférences',
    soundEffects: 'Petits sons après chaque réponse',
    speech: 'Bouton « Écouter » pour lire les mots',
    largeText: 'Grand texte',
    reducedMotion: 'Moins d’animations',
    finish: 'Créer le profil',
    nicknameTooLong: 'Le surnom est trop long (24 caractères au maximum).',
    color: 'Couleur {number}',
    colorGroup: 'Couleur du fond',
  },
  avatars: {
    owl: 'Chouette',
    cat: 'Chat',
    lion: 'Lion',
    turtle: 'Tortue',
    fish: 'Poisson',
    bee: 'Abeille',
    rabbit: 'Lapin',
    fox: 'Renard',
  },
  nav: {
    home: 'Accueil',
    subjects: 'Matières',
    rewards: 'Récompenses',
    review: 'Révisions',
    settings: 'Réglages',
    parent: 'Parent',
  },
  home: {
    greeting: 'Bonjour {name} !',
    greetingAnonymous: 'Bonjour !',
    subtitle: 'Qu’allons-nous apprendre aujourd’hui ?',
    continueTitle: 'On continue',
    continueCta: 'Allons-y',
    missionTitle: 'Mission du jour',
    missionBody: '{count} petites questions sur différents thèmes.',
    missionDone: 'Mission du jour accomplie. Bravo !',
    missionCta: 'Lancer la mission',
    missionAgain: 'S’entraîner encore',
    subjects: 'Matières',
    garden: 'Mon jardin',
    gardenHint: 'Chaque objectif maîtrisé devient une fleur dans ton jardin.',
    flowers: { zero: 'Pas encore de fleur', one: '{count} fleur', other: '{count} fleurs' },
    rewardsLink: 'Récompenses',
    reviewLink: 'Révisions',
    reviewDue: {
      zero: 'Pas de révision aujourd’hui',
      one: '{count} révision t’attend',
      other: '{count} révisions t’attendent',
    },
  },
  subjects: {
    title: 'Matières',
    objectives: { zero: 'Aucun objectif', one: '{count} objectif', other: '{count} objectifs' },
    none: 'Aucune matière n’est encore disponible pour cette classe.',
  },
  map: {
    title: 'Carte d’apprentissage',
    earlier: 'Réviser les années précédentes',
    open: 'Ouvrir',
    empty: 'Aucun objectif pour cette matière dans ta classe pour l’instant.',
  },
  mastery: {
    status: {
      notStarted: 'Pas commencé',
      learning: 'En apprentissage',
      needsPractice: 'À retravailler',
      progressing: 'En progrès',
      provisionalMastery: 'Maîtrise provisoire',
    },
    reason: {
      notStarted: 'Aucune réponse enregistrée pour l’instant.',
      gatheringEvidence:
        'Encore peu de réponses ({answered}) : il faut plus d’exercices avant toute estimation.',
      consistentAcrossDays:
        '{percent} % des dernières réponses sont justes du premier coup, sur {days} jours différents.',
      needsConfirmation:
        '{percent} % des dernières réponses sont justes. Une séance un autre jour confirmera la maîtrise.',
      mostlyCorrect: '{percent} % des dernières réponses sont justes du premier coup.',
      frequentMistakes: 'Seulement {percent} % des dernières réponses sont justes du premier coup.',
      mixedResults: 'Résultats récents variables ({percent} % justes du premier coup).',
    },
    trend: {
      improving: 'En amélioration',
      stable: 'Stable',
      declining: 'En baisse récemment',
      unknown: 'Pas encore déterminé',
    },
    estimateNote:
      'Estimation fondée sur les exercices enregistrés, pas un jugement sur les capacités de l’enfant.',
  },
  lesson: {
    practise: 'On s’entraîne',
    chooseGame: 'Choisis un jeu',
    recommended: 'Conseillé',
    noGames: 'Aucun jeu n’est encore disponible pour cet objectif.',
    workedExample: 'Exemple résolu',
    tip: 'Astuce',
    rule: 'Règle',
    examples: 'Exemples',
    status: 'Ta progression',
  },
  rules: {
    title: 'Mes règles',
    link: {
      zero: 'Mes règles',
      one: 'Mes règles : {count} règle',
      other: 'Mes règles : {count} règles',
    },
    empty: 'Pas encore de règle dans cette matière.',
    openLesson: 'Ouvrir la leçon : {lesson}',
  },
  games: {
    'quick-quiz': { name: 'Quiz rapide', description: 'De courtes questions variées.' },
    'find-correct': {
      name: 'Trouve la bonne réponse',
      description: 'Choisis la bonne réponse parmi plusieurs.',
    },
    'missing-number': {
      name: 'Le nombre manquant',
      description: 'Trouve le nombre caché dans l’opération.',
    },
    'word-builder': { name: 'Construis le mot', description: 'Remets les lettres dans l’ordre.' },
    'letter-sound': {
      name: 'Lettres et sons',
      description: 'Associe les lettres à leurs sons et à des mots.',
    },
    'sentence-builder': {
      name: 'Construis la phrase',
      description: 'Remets les mots dans l’ordre.',
    },
    memory: { name: 'Jeu de mémoire', description: 'Retourne les cartes et trouve les paires.' },
    categorize: { name: 'Jeu de tri', description: 'Range chaque élément dans son groupe.' },
    'true-false': { name: 'Vrai ou faux', description: 'Décide si la phrase est juste.' },
    'daily-mission': {
      name: 'Mission du jour',
      description: 'Un petit mélange de révisions et d’entraînement.',
    },
  },
  voice: {
    answer: 'Réponds avec ta voix',
    stop: 'Arrêter',
    listening: 'Je t’écoute… dis ta réponse.',
    heard: 'J’ai entendu : « {text} ».',
    notUnderstood: 'Je n’ai pas compris la réponse. Réessaie ou touche ta réponse.',
    error: {
      'not-allowed':
        'Le micro n’est pas autorisé. Demande à un adulte de l’autoriser dans le navigateur.',
      'no-speech': 'Je n’ai rien entendu. Parle clairement près de l’appareil.',
      unavailable: 'La réponse à voix haute n’est pas disponible dans ce navigateur.',
      aborted: 'L’écoute est arrêtée.',
      failed:
        'La reconnaissance vocale ne marche pas pour le moment. Tu peux répondre en touchant.',
    },
    setting: 'Répondre à voix haute (test)',
    settingDescription:
      'Une fois activé, votre enfant peut répondre à certaines questions avec sa voix. C’est le navigateur qui transforme la voix en texte : dans Chrome et sur Android, la voix est envoyée à Google pour cela ; dans Safari, à Apple ou traitée sur l’appareil. {app} n’enregistre pas la voix, ne la garde pas et ne l’envoie pas à son serveur.',
    unsupported: 'Ce navigateur ne reconnaît pas la voix. Essayez Chrome ou Safari.',
  },
  vote: {
    title: 'Votez pour la prochaine version',
    nav: 'Voter',
    intro:
      'Choisissez jusqu’à 3 idées pour {app}. Les plus demandées sont faites en premier et annoncées dans « Quoi de neuf ? ». Vous pouvez choisir avec vos enfants.',
    choose: 'Choisissez jusqu’à {max} idées',
    chosen: '{count} sur {max}',
    ideaLabel: 'Une autre idée ? Écrivez-la ici (facultatif)',
    ideaHint: 'N’écrivez pas de noms ni d’informations personnelles sur les enfants.',
    send: 'Envoyer',
    thanks: 'Votre vote est bien reçu. Merci de nous aider à choisir la suite !',
    alreadyVoted: 'Vous avez déjà voté pour ce tour. Merci !',
    alreadyVotedHint:
      'Un nouveau tour s’ouvre avec la prochaine version. Vous pouvez toujours envoyer une idée.',
    ideas: {
      'journey-map': {
        title: 'La carte du voyage',
        description: 'Le 4x4 ObiGo roule à travers la Tunisie à chaque objectif maîtrisé.',
      },
      'recorded-audio': {
        title: 'Des voix enregistrées',
        description: 'Une enseignante tunisienne prononce les lettres, les sons et les mots.',
      },
      'teacher-review': {
        title: 'Relu par des enseignants',
        description: 'Des enseignants vérifient chaque question et chaque leçon.',
      },
      'home-tips': {
        title: 'Idées pour la maison',
        description: 'Une petite activité à la maison pour chaque difficulté du rapport.',
      },
      printables: {
        title: 'Fiches à imprimer',
        description: 'Le rapport de la semaine et des exercices en PDF.',
      },
      stories: {
        title: 'Histoires illustrées',
        description: 'De courtes histoires en Tunisie, avec des jeux de compréhension.',
      },
      'play-together': {
        title: 'Jouer ensemble',
        description:
          'Un parent ou un frère pose la question, l’enfant répond sur le même appareil.',
      },
      'reading-comfort': {
        title: 'Lecture confortable',
        description: 'Des options pour la dyslexie et des couleurs plus contrastées.',
      },
      'packs-on-demand': {
        title: 'Téléchargement par année',
        description: 'Ne télécharger que l’année de l’enfant pour garder l’application légère.',
      },
      'content-stats': {
        title: 'Statistiques anonymes (avec accord)',
        description: 'Pour trouver vite les questions trop difficiles ou fausses.',
      },
      'lighter-app': {
        title: 'Une application plus rapide',
        description: 'Plus rapide sur les téléphones simples et les connexions lentes.',
      },
    },
  },
  whatsNew: {
    title: 'Quoi de neuf ?',
    intro: 'Ce qui change à chaque version, souvent grâce à vos remarques.',
    version: '{app} — version {version}',
    latest: 'La plus récente',
    banner: 'Nouveau dans {app} (version {version})',
    seeAll: 'Voir toutes les nouveautés',
    dismiss: 'D’accord',
    voteHint: 'Que voulez-vous dans la prochaine version ?',
    voteLink: 'Votez et partagez vos idées (espace parent)',
  },
  play: {
    progress: 'Question {current} sur {total}',
    check: 'Vérifier',
    hint: 'Indice',
    hintTitle: 'Indice',
    correct1: 'Bravo !',
    correct2: 'Super, c’est juste !',
    correct3: 'Excellent !',
    correctAfterRetry: 'Bravo ! Le deuxième essai était le bon.',
    tryAgain: 'Ce n’est pas encore ça. Essaie encore !',
    tryAgainButton: 'Réessayer',
    revealed: 'Ce n’est pas grave, on apprend ensemble ! La bonne réponse :',
    revealedNoAnswer: 'Ce n’est pas grave, on apprend ensemble ! Regarde la solution :',
    explanation: 'Comment réfléchir ?',
    showExplanation: 'Explique-moi',
    hideExplanation: 'Masquer l’explication',
    next: 'Question suivante',
    finish: 'Terminer',
    quit: 'Quitter',
    quitTitle: 'Veux-tu quitter ?',
    quitBody: 'Les réponses déjà données seront enregistrées.',
    quitConfirm: 'Oui, quitter',
    stay: 'Je continue',
    listen: 'Écouter',
    stop: 'Arrêter',
    true: 'Vrai',
    false: 'Faux',
    yourAnswer: 'Ta réponse',
    clear: 'Effacer',
    backspace: 'Effacer le dernier chiffre',
    answerArea: 'Zone de réponse',
    tilesArea: 'Lettres et mots disponibles',
    tapToPlace: 'Touche les cartes dans l’ordre. Touche une carte de la réponse pour la remettre.',
    emptyAnswer: 'Réponds d’abord, puis appuie sur « Vérifier ».',
    matchHelp: 'Choisis un élément à gauche, puis celui qui va avec à droite.',
    matchHelpLtr:
      'Choisis un élément dans la première liste, puis celui qui va avec dans la seconde.',
    matched: 'Associé à {item}',
    pairNumber: 'Paire {number}',
    memoryHelp: 'Retourne deux cartes à la fois.',
    memoryCard: 'Carte {number}',
    memoryFound: '{found} trouvées sur {total}',
    categorizeHelp: 'Choisis un élément, puis touche le bon groupe.',
    selected: 'sélectionné',
    putIn: 'Mettre dans « {category} »',
    unsorted: 'Éléments à trier',
    wrongParts: 'Certaines réponses sont à revoir : regarde les marques.',
    preview: 'Le mot',
    noExercises: 'Aucune question n’est encore disponible pour ce jeu.',
    saving: 'Enregistrement…',
    numberLine: 'Droite graduée de {min} à {max}',
    countAria: '{count} × {label}',
    blank: 'case vide',
  },
  results: {
    title: 'Séance terminée !',
    stars: 'Tu as obtenu {count} étoiles sur 3',
    summary: 'Réponses justes : {correct} sur {total}.',
    firstTry: 'Dont {count} du premier coup.',
    encourage3: 'Travail soigné, bravo !',
    encourage2: 'Beau progrès, continue !',
    encourage1: 'Chaque essai te rend plus fort. On s’entraînera encore ensemble.',
    newAchievements: 'Nouvelle récompense !',
    playAgain: 'Rejouer',
    backToMap: 'Retour à la carte',
    home: 'Accueil',
    nextStep: 'Étape suivante',
  },
  rewards: {
    title: 'Mes récompenses',
    earned: 'Obtenues',
    locked: 'Pas encore obtenues',
    progress: '{current} sur {target}',
    garden: 'Mon jardin',
    gardenEmpty:
      'Entraîne-toi sur un objectif plusieurs jours pour voir pousser ta première fleur.',
  },
  achievements: {
    'first-steps': { title: 'Premier pas', description: 'Termine ta première séance.' },
    'math-explorer': {
      title: 'Explorateur des nombres',
      description: 'Termine 5 séances de mathématiques.',
    },
    'letter-friend': { title: 'Ami des lettres', description: 'Termine 5 séances d’arabe.' },
    'curious-scientist': {
      title: 'Scientifique curieux',
      description: 'Termine 5 séances d’éveil scientifique.',
    },
    'globe-trotter': {
      title: 'Grand voyageur',
      description: 'Entraîne-toi dans 3 matières différentes.',
    },
    'never-give-up': {
      title: 'Je n’abandonne pas',
      description: 'Réussis au deuxième essai 10 fois.',
    },
    'good-helper': {
      title: 'J’utilise les indices',
      description: 'Utilise un indice puis réponds juste 5 fois.',
    },
    'first-flower': { title: 'Première fleur', description: 'Maîtrise ton premier objectif.' },
    gardener: { title: 'Jardinier', description: 'Maîtrise 5 objectifs.' },
    'mission-1': { title: 'Première mission', description: 'Termine ta première mission du jour.' },
    'mission-7': {
      title: 'Sept missions',
      description: 'Termine 7 missions du jour (quand tu veux).',
    },
    reviewer: { title: 'Révisions actives', description: 'Termine 3 séances de révision.' },
    'careful-work': { title: 'Travail soigné', description: 'Obtiens 3 étoiles dans une séance.' },
  },
  review: {
    title: 'Révisions conseillées',
    intro: 'Ces objectifs méritent un petit entraînement maintenant.',
    empty: 'Rien à réviser pour l’instant. Reviens plus tard !',
    start: 'Commencer',
  },
  recommend: {
    kind: {
      review: 'Révision',
      prerequisite: 'Base importante',
      easier: 'Entraînement en douceur',
      practice: 'On continue',
      next: 'Nouvel objectif',
      start: 'Commençons',
    },
    reason: {
      review: {
        zero: 'Entraînement fait aujourd’hui. Une courte révision aide à retenir.',
        one: 'Dernier entraînement il y a {count} jour. Une courte révision aide à retenir.',
        other: 'Dernier entraînement il y a {count} jours. Une courte révision aide à retenir.',
      },
      prerequisite:
        'L’objectif « {objective} » demande plus d’entraînement, et celui-ci en est une base.',
      easier:
        'Les résultats récents ({percent} %) suggèrent des exercices plus simples avec des indices.',
      practice:
        '{percent} % des dernières réponses sont justes. Encore un peu d’entraînement consolidera l’apprentissage.',
      next: 'Les objectifs précédents progressent bien : voici le suivant.',
      start: 'Premier objectif de cette matière.',
    },
  },
  mistakes: {
    'off-by-one': 'résultat décalé de un (comptage)',
    'wrong-operation': 'utilisation d’une autre opération',
    'place-value': 'confusion entre unités, dizaines et centaines',
    regrouping: 'retenues et échanges de dizaines',
    'smaller-from-larger': 'soustraire le plus petit chiffre du plus grand dans chaque colonne',
    'table-slip': 'confusion entre résultats voisins des tables',
    counting: 'dénombrement',
    'similar-letter-shape': 'lettres de forme proche',
    'similar-sound': 'sons proches',
    'letter-position': 'formes de la lettre selon sa place',
    harakat: 'voyelles courtes',
    'letter-order': 'ordre des lettres',
    'word-order': 'ordre des mots',
    spelling: 'orthographe',
    article: 'choix de l’article',
    agreement: 'accords (singulier, pluriel)',
    conjugation: 'conjugaison',
    vocabulary: 'sens des mots',
    comprehension: 'compréhension du texte',
    'grammar-role': 'fonctions des mots dans la phrase',
    classification: 'classement',
    misconception: 'idée scientifique à clarifier',
    unknown: 'erreurs variées',
  },
  parent: {
    gate: {
      title: 'Espace parent',
      intro: 'Saisissez le code parent.',
      pin: 'Code parent',
      unlock: 'Entrer',
      wrong: {
        zero: 'Code incorrect.',
        one: 'Code incorrect. Il reste {count} essai.',
        other: 'Code incorrect. Il reste {count} essais.',
      },
      locked: 'Trop d’essais. Réessayez dans {seconds} secondes.',
      forgot: 'Code oublié ?',
      checking: 'Vérification…',
      childNote: 'Cet espace est réservé aux parents.',
    },
    setup: {
      title: 'Créer un code parent',
      intro: 'Ce code évite qu’un enfant entre par mégarde dans l’espace parent.',
      pin: 'Code de 4 à 6 chiffres',
      confirm: 'Saisissez à nouveau le code',
      mismatch: 'Les deux codes ne correspondent pas.',
      format: 'Le code comporte 4 à 6 chiffres.',
      tooSimple: 'Choisissez un code moins évident que des chiffres répétés ou qui se suivent.',
      create: 'Créer le code',
      limitation:
        'Attention : ce code tient les enfants à l’écart, mais ne protège pas les données contre une personne ayant un accès complet à l’appareil.',
    },
    recovery: {
      title: 'Code de récupération',
      intro:
        'Conservez ce code en lieu sûr (sur papier par exemple). Il sert si vous oubliez le code parent et ne sera plus affiché.',
      copy: 'Copier',
      copied: 'Copié',
      saved: 'J’ai noté le code de récupération en lieu sûr',
      continue: 'Continuer',
    },
    reset: {
      title: 'Récupérer l’accès',
      code: 'Code de récupération',
      newPin: 'Nouveau code parent',
      submit: 'Changer le code',
      wrongCode: 'Code de récupération incorrect.',
    },
    dashboard: {
      title: 'Espace parent',
      lock: 'Verrouiller',
      learners: 'Élèves',
      noLearners: 'Aucun profil pour l’instant.',
      open: 'Voir le rapport',
      dataLink: 'Données et vie privée',
      settingsLink: 'Sécurité',
    },
    report: {
      title: 'Rapport de {name}',
      anonymousChild: 'votre enfant',
      level: 'Classe : {level}',
      estimateNote:
        'Tous les chiffres proviennent des exercices enregistrés sur cet appareil uniquement. Ce sont des estimations de progression, pas une évaluation scolaire.',
      learningTime: 'Temps d’apprentissage',
      sessions: 'Séances terminées',
      exercises: 'Questions répondues',
      accuracy: 'Justes du premier coup',
      thisWeek: 'Cette semaine',
      allTime: 'Depuis le début',
      bySubject: 'Par matière',
      answered: 'Questions : {count}',
      mastered: 'Objectifs en maîtrise provisoire',
      needsPractice: 'Objectifs à retravailler',
      inProgress: 'Objectifs en cours',
      none: 'Rien pour l’instant.',
      improvement: 'Évolution par semaine',
      weekOf: 'Semaine du {date}',
      notEnoughWeeks: 'Il faut au moins deux semaines d’exercices pour afficher une évolution.',
      recentSessions: 'Dernières séances',
      noSessions: 'Aucune séance enregistrée.',
      recommended: 'Activités conseillées',
      weekly: 'Bilan de la semaine',
      print: 'Imprimer le rapport',
      export: 'Exporter les données de cet élève',
      accuracyTable: 'Tableau de réussite par matière',
      chartAlt: 'Part des réponses justes du premier coup chaque semaine',
      abandoned: 'Séance non terminée',
      why: 'Pourquoi ?',
    },
    summary: {
      practised: '{name} s’est entraîné(e) sur : {objectives}.',
      strong:
        '« {objective} » : réponses justes la plupart du temps ({correct} sur {answered} du premier coup).',
      developing: '« {objective} » : en progrès ({correct} sur {answered} du premier coup).',
      needsPractice: '« {objective} » demande encore de l’entraînement ({correct} sur {answered}).',
      needsPracticeMistake:
        '« {objective} » demande encore de l’entraînement, en particulier : {mistake}.',
      fewAnswers: 'Estimation préliminaire : encore peu de réponses ({count}).',
      mastery: 'Maîtrise provisoire de « {objective} » : résultats stables sur plusieurs jours.',
      perseverance: 'Réponses corrigées au deuxième essai : {count}. Belle persévérance !',
      nextStep: 'Étape conseillée : {step}',
      weekSessions:
        'Cette semaine — séances : {sessions}, temps d’apprentissage : environ {minutes}, questions : {answered}.',
      weekAccuracy: '{percent} % des réponses justes du premier coup.',
      weekMastered: 'Nouveaux objectifs en maîtrise provisoire : {objectives}.',
      weekNeeds: 'À retravailler : {objectives}.',
      weekEmpty: 'Aucune séance cette semaine.',
    },
    security: {
      title: 'Sécurité',
      changePin: 'Changer le code parent',
      currentPin: 'Code actuel',
      newPin: 'Nouveau code',
      pinChanged: 'Le code a été changé.',
      rotate: 'Créer un nouveau code de récupération',
      rotateHint: 'L’ancien code de récupération ne fonctionnera plus.',
    },
  },
  settings: {
    title: 'Réglages',
    language: 'Langue de l’application',
    preferences: 'Préférences de {name}',
    deviceLanguageHint: 'Cette langue est utilisée avant de choisir un élève.',
    profile: 'Profil',
    level: 'Classe',
    editProfile: 'Enregistrer',
    saved: 'Enregistré.',
    privacyLink: 'Vie privée et données',
    contentStatus: 'État du contenu',
    contentVersion: 'Version du contenu : {version}',
  },
  privacy: {
    title: 'Vie privée et données',
    intro: '{app} fonctionne sans compte et sans collecte de données personnelles.',
    stored:
      'Ce qui est enregistré : un profil avec un identifiant aléatoire, la classe, un surnom facultatif, les réponses et la progression. Uniquement sur cet appareil.',
    notStored:
      'Nous ne demandons ni vrai nom, ni date de naissance, ni nom d’école, ni moyen de contact, et rien n’est envoyé à un serveur.',
    noTrackers: 'Aucune publicité, aucun outil d’analyse ou de suivi.',
    localWarningTitle: 'Important : données locales',
    localWarning:
      'Effacer les données du navigateur, désinstaller l’application ou perdre l’appareil supprime toute la progression. Exportez régulièrement une sauvegarde.',
    persist: 'Demander un stockage durable au navigateur',
    persistGranted: 'Le navigateur a accepté le stockage durable.',
    persistDenied: 'Le navigateur a refusé. Exportez régulièrement une sauvegarde.',
    persistUnsupported: 'Ce navigateur ne permet pas de demander un stockage durable.',
    backupTitle: 'Sauvegarde',
    backupHowTo:
      'Appuyez sur « Exporter » pour télécharger un fichier. Rangez-le dans un endroit privé (il n’est pas chiffré). Pour le restaurer sur cet appareil ou un autre, utilisez « Importer ».',
    exportAll: 'Exporter toutes les données',
    exported: 'Le fichier {file} a été créé.',
    importTitle: 'Importer une sauvegarde',
    importChoose: 'Choisir un fichier de sauvegarde (.json)',
    importPreview: {
      zero: 'Le fichier ne contient aucun élève.',
      one: 'Le fichier contient {count} élève.',
      other: 'Le fichier contient {count} élèves.',
    },
    importConflict: 'Certains de ces profils existent déjà sur cet appareil.',
    importReplace: 'Remplacer l’existant',
    importCopy: 'Ajouter comme copie',
    importConfirm: 'Importer',
    importDone: 'Import terminé.',
    importErrors: {
      tooLarge: 'Le fichier est trop volumineux.',
      notJson: 'Ce fichier n’est pas une sauvegarde valide.',
      wrongFormat: 'Ce fichier ne provient pas de {app}.',
      newerVersion:
        'Ce fichier vient d’une version plus récente de {app}. Mettez d’abord l’application à jour.',
      invalid: 'Le fichier est endommagé ou incomplet ; rien n’a été modifié.',
      inconsistent: 'Le contenu du fichier est incohérent ; rien n’a été modifié.',
      empty: 'Le fichier est vide.',
      readFailed: 'Le fichier n’a pas pu être lu.',
    },
    deleteTitle: 'Supprimer des données',
    deleteLearner: 'Supprimer le profil de {name}',
    deleteLearnerBody:
      'Le profil, ses réponses et ses récompenses seront définitivement supprimés de cet appareil.',
    deleteAll: 'Tout supprimer',
    deleteAllBody:
      'Tous les profils, la progression et le code parent seront définitivement supprimés de cet appareil.',
    typeToConfirm: 'Tapez « {word} » pour confirmer',
    confirmWord: 'SUPPRIMER',
    deleted: 'Supprimé.',
  },
  feedback: {
    rateTitle: 'As-tu aimé ce jeu ?',
    liked: 'J’ai aimé',
    soso: 'Bof',
    disliked: 'Je n’ai pas aimé',
    rateThanks: 'Merci ! Ton parent verra ton avis.',
    report: 'Signaler un problème',
    reportTitle: 'Quel est le problème avec cette question ?',
    reasons: {
      unclear: 'La question n’est pas claire',
      wrongAnswer: 'Je pense que la réponse est fausse',
      tooHard: 'C’est trop difficile',
      something: 'Autre chose',
    },
    reportSend: 'L’envoyer à mon parent',
    reportThanks: 'Merci ! Ton parent verra ta remarque.',
  },
  parentFeedback: {
    title: 'Avis et suggestions',
    intro:
      'Votre avis nous aide à améliorer {app}. Nous lisons chaque message et corrigeons les erreurs dans les prochaines mises à jour.',
    privacyNote:
      'Nous n’envoyons ni identifiant, ni surnom, ni résultat des enfants : seulement ce que vous écrivez ici et les réponses toutes faites choisies par les enfants.',
    childrenTitle: 'Ce que les enfants ont signalé',
    childrenEmpty: 'Aucune nouvelle remarque des enfants.',
    includeChildren: 'Joindre les remarques des enfants',
    ratingEntry: '« {game} » : {mood}',
    problemEntry: 'Question de « {objective} » : {reason}',
    formTitle: 'Votre message',
    role: 'Je suis',
    roles: { parent: 'parent', teacher: 'enseignant(e)', another: 'autre' },
    topic: 'Sujet',
    topics: {
      idea: 'Idée ou suggestion',
      problem: 'Problème dans l’application',
      content: 'Erreur dans un contenu',
      praise: 'Avis positif',
      misc: 'Autre sujet',
    },
    message: 'Message',
    messageHint: 'Merci de ne pas écrire de nom ni d’information personnelle sur l’enfant.',
    contact: 'E-mail de contact (facultatif)',
    contactHint: 'Laissez vide si vous n’attendez pas de réponse. Il ne sert à rien d’autre.',
    technical: 'Joindre des informations techniques anonymes (versions, langue, taille d’écran)',
    preview: 'Ce qui sera envoyé, exactement',
    send: 'Envoyer',
    sending: 'Envoi…',
    sent: 'Votre message est bien arrivé. Merci !',
    empty: 'Écrivez un message ou joignez les remarques des enfants avant d’envoyer.',
    errorOffline: 'Pas de connexion Internet. Réessayez plus tard ou copiez le message.',
    errorRejected: 'Le message n’a pas pu être accepté (il est peut-être trop long).',
    errorServer:
      'Le service est indisponible pour le moment. Réessayez plus tard ou copiez le message.',
    copy: 'Copier le message',
    copied: 'Copié',
    email: 'Envoyer par e-mail',
  },
  units: {
    minutes: { zero: 'moins d’une minute', one: '{count} minute', other: '{count} minutes' },
  },
};
